import {
  Box,
  Columns,
  Container,
  Divider,
  Heading,
  Stack,
  Text,
} from '@spaceship-fspl/components';
import { FeatherInfoIcon } from '@spaceship-fspl/icons-web';
import {
  Color,
  marginBottom,
  marginTop,
  match,
  ResponsiveValue,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { Card, PanelSection } from 'components';
import { Super_PortfolioBodyFeesFields } from 'data/prismic/types';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import feesBackgroundImage from './fees-bg.svg';

interface Props {
  title: string;
  portfolioTitle: string;
  feeTableTitle: string;
  fees?: Super_PortfolioBodyFeesFields[] | null;
  example?: string | React.ReactNode;
  disclaimer?: string | React.ReactNode;
  backgroundColor?: ResponsiveValue<Color>;
}

export const PortfolioFeesPanel: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  feeTableTitle,
  portfolioTitle,
  fees,
  example,
  disclaimer,
  ...props
}) => {
  return (
    <TrackingCategory category="Fees Panel">
      <PanelSection {...props}>
        <Container>
          <Stack spaceY={{ xs: 'lg', md: 'xl', lg: 'xxl' }}>
            <Stack spaceY={{ xs: 'md', md: 'lg', lg: 'xl' }}>
              <Columns alignX="center">
                <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 1 / 2 }}>
                  <Heading
                    component="h2"
                    variant={2}
                    color="neutral.100"
                    align="center"
                  >
                    {title}
                  </Heading>
                </Columns.Column>
              </Columns>
            </Stack>
            <Columns alignX="center">
              <BackgroundContainer>
                <BackgroundImage />
              </BackgroundContainer>
              <Columns.Column width={{ xs: 1, lg: 1 / 2 }}>
                <Card
                  variant="light"
                  padding={{ xs: 'md', md: 'xl', lg: 'md' }}
                >
                  <Stack spaceY={{ xs: 'md' }}>
                    <Box padding={{ xs: 'none', lg: 'md' }}>
                      <Stack spaceY={{ xs: 'md' }}>
                        <TableTitle>
                          {portfolioTitle} – {feeTableTitle}
                        </TableTitle>
                        {fees
                          ?.filter(
                            (f) =>
                              !f?.fee_type
                                ?.toLowerCase()
                                .includes('cost of product'),
                          )
                          ?.map((f, index) => (
                            <Fragment key={index}>
                              <Divider
                                color="neutral.050"
                                size={index === 0 ? 'md' : 'sm'}
                              />
                              <Stack spaceY="xs">
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Text
                                    variant={{ xs: 1, md: 2, lg: 1 }}
                                    color="neutral.085"
                                  >
                                    {f.fee_type}
                                  </Text>
                                  <Text
                                    variant={{ xs: 1, md: 2, lg: 1 }}
                                    color="neutral.100"
                                    align="right"
                                  >
                                    {f.fee}
                                  </Text>
                                </Box>
                                {f.fee_note && (
                                  <Text variant={2} color="neutral.085">
                                    {f.fee_note}
                                  </Text>
                                )}
                                {f.third_party_fee_note && (
                                  <Text variant={4} color="neutral.085">
                                    {f.third_party_fee_note}
                                  </Text>
                                )}
                              </Stack>
                            </Fragment>
                          ))}
                      </Stack>
                    </Box>

                    <Box
                      backgroundColor="neutral.015"
                      borderRadius="sm"
                      paddingX={{ xs: 'md', md: 'lg', lg: 'xl' }}
                      paddingY={{ xs: 'md', md: 'md', lg: 'lg' }}
                    >
                      <Stack spaceY="lg">
                        <Stack spaceY="sm">
                          <Box display="flex" justifyContent="center">
                            <FeatherInfoIcon
                              color="indigo.070"
                              size="lg"
                              strokeWidth={2}
                            />
                          </Box>
                          <StyledExample>{example}</StyledExample>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              </Columns.Column>
            </Columns>

            <Columns alignX="center">
              <Columns.Column width={{ lg: 9 / 12 }}>
                <StyledDisclaimer>{disclaimer}</StyledDisclaimer>
              </Columns.Column>
            </Columns>
          </Stack>
        </Container>
      </PanelSection>
    </TrackingCategory>
  );
};

const BackgroundContainer = styled(Columns.Column).attrs({
  width: { xs: 7 / 8, lg: 9 / 12 },
})<{ width?: object }>`
  position: relative;
`;

const BackgroundImage = styled('div')`
  background: url(${feesBackgroundImage}) no-repeat;
  background-position: center;
  background-size: contain;
  padding-top: 58%;
  position: absolute;
  top: -10px;
  left: 20px;
  width: 95%;

  ${match('lg')`
    top: -50px;
  `}
`;

const TableTitle = styled(Text).attrs({
  variant: 1,
  isBold: true,
  component: 'h3',
  align: 'center',
})<{ variant?: number; isBold?: boolean; component?: string; align?: string }>`
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
`;

const StyledExample = styled(Text).attrs({
  variant: 2,
  align: 'center',
  color: 'neutral.100',
  component: 'div',
})<{ variant?: number; align?: string; color?: string; component?: string }>`
  p:not(:first-child) {
    ${marginTop('md')}
  }
`;

const StyledDisclaimer = styled(Text).attrs({
  variant: 3,
  align: 'center',
  color: 'neutral.085',
  component: 'div',
})<{ variant?: number; align?: string; color?: string; component?: string }>`
  p {
    ${marginBottom('sm')}
  }
`;
